import { formatDateTime, formatDate } from '@/utils/datetime';
import { REPORT_STYLE, dash, printReport } from '@/pages/Batch-Processing/reports/reportStyle';

const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const fmtDt = (v) => (v ? formatDateTime(v, 'DD-MMM-YYYY HH:mm') : '—');
const fmtDate = (v) => (v ? formatDate(v) : '—');

const statusClass = (status) => {
  const s = String(status || '').toLowerCase();
  if (s.includes('complete')) return 'completed';
  if (s.includes('progress') || s.includes('running')) return 'in-progress';
  if (s.includes('reject') || s.includes('fail')) return 'rejected';
  return 'pending';
};

const statusPill = (status) =>
  `<span class="status ${statusClass(status)}">${esc(status || '—')}</span>`;

const sectionTitle = (title) =>
  `<div class="section-title"><table><tbody><tr><th class="header-class">${esc(title)}</th></tr></tbody></table></div>`;

const emptyRow = (cols, msg = 'No data') =>
  `<tr><td colspan="${cols}" class="table_data">${esc(msg)}</td></tr>`;

/**
 * Build A4 Batch LogBook HTML from get_batch_ebook payload and open print → PDF.
 */
export function exportBatchLogbookPdf(ebookData, { mbmrAlias = 'MBMR', ingredientsAlias = 'Ingredients' } = {}) {
  if (!ebookData) return false;

  const approvals = ebookData.bmr_approvals || {};
  const approvalRows = [
    { label: `${mbmrAlias} Creation`, data: approvals.mbmr_creation },
    { label: `${mbmrAlias} Approval`, data: approvals.mbmr_approval },
    { label: 'BMR Release Approval', data: approvals.bmr_release_approval },
    { label: 'BMR Release', data: approvals.bmr_release },
  ].filter((a) => a.data);

  let approvalsHtml = '';
  if (approvalRows.length) {
    approvalsHtml =
      sectionTitle('BMR Approval') +
      `<table>
        <thead><tr class="header-class-1"><th>Stage</th><th>User</th><th>Timestamp</th></tr></thead>
        <tbody>
          ${approvalRows
            .map(
              (a) =>
                `<tr>
                  <td>${esc(a.label)}</td>
                  <td class="table_data">${esc(a.data.user || '—')}</td>
                  <td class="table_data">${esc(fmtDt(a.data.timestamp))}</td>
                </tr>`
            )
            .join('')}
        </tbody>
      </table>`;
  }

  const processes = ebookData.processes || [];
  let processesHtml = '';

  if (!processes.length) {
    processesHtml = sectionTitle('Processes') + `<p>No processes found for this batch.</p>`;
  } else {
    processes.forEach((process, pIdx) => {
      processesHtml += sectionTitle(`Process ${pIdx + 1}: ${process.process_name || '—'}`);
      processesHtml += `<table>
        <tbody>
          <tr>
            <td><b>Status:</b> ${statusPill(process.status)}</td>
            <td><b>Assigned To:</b> ${esc(dash(process.assigned_to))}</td>
            <td><b>Start:</b> ${esc(fmtDt(process.start_time))}</td>
            <td><b>End:</b> ${esc(fmtDt(process.end_time))}</td>
          </tr>
        </tbody>
      </table>`;

      const ops = process.operations || [];
      processesHtml += `<table>
        <thead>
          <tr class="header-class-1">
            <th style="width:10%">Op No</th>
            <th style="width:18%">Operation</th>
            <th style="width:10%">Type</th>
            <th style="width:10%">Status</th>
            <th style="width:12%">Equipment</th>
            <th style="width:12%">Start</th>
            <th style="width:12%">End</th>
            <th style="width:8%">Duration</th>
            <th style="width:8%">Performed By</th>
          </tr>
        </thead>
        <tbody>
          ${
            ops.length
              ? ops
                  .map(
                    (op) =>
                      `<tr>
                        <td class="table_data">${esc(op.operation_number)}</td>
                        <td>${esc(op.operation_name)}</td>
                        <td class="table_data">${esc(op.operation_type)}</td>
                        <td class="table_data">${statusPill(op.status)}</td>
                        <td class="table_data">${esc(op.machine_name || op.machine_code || '—')}</td>
                        <td class="table_data">${esc(fmtDt(op.start_time))}</td>
                        <td class="table_data">${esc(fmtDt(op.end_time))}</td>
                        <td class="table_data">${esc(op.duration != null ? `${op.duration} min` : '—')}</td>
                        <td class="table_data">${esc(op.performed_by || '—')}</td>
                      </tr>`
                  )
                  .join('')
              : emptyRow(9, 'No operations')
          }
        </tbody>
      </table>`;

      ops.forEach((op) => {
        const ingredients = op.ingredients || [];
        const parameters = op.parameters || [];
        const subOps = op.sub_operations || [];
        const products = op.products || [];
        const hasDetail =
          ingredients.length || parameters.length || subOps.length || products.length;
        if (!hasDetail) return;

        processesHtml += `<div class="card" style="margin-top:12px;">
          <h2>Operation Detail — ${esc(op.operation_number)} ${esc(op.operation_name)}</h2>
          <p><b>Verified By:</b> ${esc(op.verified_by || '—')}</p>`;

        if (ingredients.length) {
          processesHtml += `<h3 style="font-size:13px;color:#1a73e8;margin:12px 0 4px;">${esc(ingredientsAlias)}</h3>
            <table>
              <thead><tr class="header-class-1"><th>Code</th><th>Material</th><th>Required</th><th>Actual</th><th>UOM</th><th>Deviation</th><th>Performed By</th></tr></thead>
              <tbody>
                ${ingredients
                  .map(
                    (ing) =>
                      `<tr>
                        <td class="table_data">${esc(ing.material_code)}</td>
                        <td>${esc(ing.material_name)}</td>
                        <td class="table_data">${esc(ing.required_qty)}</td>
                        <td class="table_data">${esc(ing.actual_qty)}</td>
                        <td class="table_data">${esc(ing.uom)}</td>
                        <td class="table_data">${esc(ing.deviation)}</td>
                        <td class="table_data">${esc(ing.performed_by)}</td>
                      </tr>`
                  )
                  .join('')}
              </tbody>
            </table>`;
        }

        if (parameters.length) {
          processesHtml += `<h3 style="font-size:13px;color:#1a73e8;margin:12px 0 4px;">Parameters</h3>
            <table>
              <thead><tr class="header-class-1"><th>Parameter</th><th>Set</th><th>Actual</th><th>Min</th><th>Max</th><th>UOM</th><th>Status</th><th>Performed By</th></tr></thead>
              <tbody>
                ${parameters
                  .map(
                    (p) =>
                      `<tr>
                        <td>${esc(p.parameter_name)}</td>
                        <td class="table_data">${esc(dash(p.set_value))}</td>
                        <td class="table_data">${esc(dash(p.actual_value))}</td>
                        <td class="table_data">${esc(dash(p.min_limit))}</td>
                        <td class="table_data">${esc(dash(p.max_limit))}</td>
                        <td class="table_data">${esc(p.uom || '—')}</td>
                        <td class="table_data">${statusPill(p.status)}</td>
                        <td class="table_data">${esc(p.performed_by || '—')}</td>
                      </tr>`
                  )
                  .join('')}
              </tbody>
            </table>`;
        }

        if (subOps.length) {
          processesHtml += `<h3 style="font-size:13px;color:#1a73e8;margin:12px 0 4px;">Sub-operations</h3>
            <table>
              <thead><tr class="header-class-1"><th>#</th><th>Name</th><th>Status</th><th>Performed By</th><th>Start</th><th>End</th><th>Remarks</th></tr></thead>
              <tbody>
                ${subOps
                  .map(
                    (s, i) =>
                      `<tr>
                        <td class="table_data">${i + 1}</td>
                        <td>${esc(s.sub_operation_name)}</td>
                        <td class="table_data">${statusPill(s.status)}</td>
                        <td class="table_data">${esc(s.performed_by)}</td>
                        <td class="table_data">${esc(fmtDt(s.start_time))}</td>
                        <td class="table_data">${esc(fmtDt(s.end_time))}</td>
                        <td>${esc(s.remarks || '—')}</td>
                      </tr>`
                  )
                  .join('')}
              </tbody>
            </table>`;
        }

        if (products.length) {
          processesHtml += `<h3 style="font-size:13px;color:#1a73e8;margin:12px 0 4px;">Products / Output</h3>
            <table>
              <thead><tr class="header-class-1"><th>Code</th><th>Product</th><th>Quantity</th><th>UOM</th><th>Container</th></tr></thead>
              <tbody>
                ${products
                  .map(
                    (pr) =>
                      `<tr>
                        <td class="table_data">${esc(pr.product_code)}</td>
                        <td>${esc(pr.product_name)}</td>
                        <td class="table_data">${esc(pr.quantity)}</td>
                        <td class="table_data">${esc(pr.uom)}</td>
                        <td class="table_data">${esc(pr.container_id || '—')}</td>
                      </tr>`
                  )
                  .join('')}
              </tbody>
            </table>`;
        }

        processesHtml += `</div>`;
      });
    });
  }

  const recipeSigs = ebookData.audit_trail?.recipe_signatures || [];
  const batchSigs = ebookData.audit_trail?.batch_signatures || [];
  let auditHtml = '';
  if (recipeSigs.length || batchSigs.length) {
    auditHtml = sectionTitle('E-Signature Audit Trail');
    const rows = [
      ...recipeSigs.map((s) => ({ ...s, scope: 'Recipe' })),
      ...batchSigs.map((s) => ({ ...s, scope: 'Batch' })),
    ];
    auditHtml += `<table>
      <thead><tr class="header-class-1"><th>Scope</th><th>Action</th><th>Category</th><th>Performed By</th><th>Timestamp</th><th>Comments</th></tr></thead>
      <tbody>
        ${rows
          .map(
            (s) =>
              `<tr>
                <td class="table_data">${esc(s.scope)}</td>
                <td>${esc(s.action_type)}</td>
                <td class="table_data">${esc(s.category || '—')}</td>
                <td class="table_data">${esc(s.performed_by)}</td>
                <td class="table_data">${esc(fmtDt(s.timestamp))}</td>
                <td>${esc(s.comments || '—')}</td>
              </tr>`
          )
          .join('')}
      </tbody>
    </table>`;
  }

  const html = `
    <div class="bmr-report">
      <div class="container">
        <header>
          <h1>Batch LogBook Report</h1>
          <p>${esc(ebookData.batch_number || '')}</p>
        </header>

        ${sectionTitle('Batch Details')}
        <table>
          <tbody>
            <tr>
              <td><b>Batch Number:</b> ${esc(dash(ebookData.batch_number))}</td>
              <td><b>${esc(mbmrAlias)} Name:</b> ${esc(dash(ebookData.recipe_name))}</td>
              <td><b>${esc(mbmrAlias)} Code:</b> ${esc(dash(ebookData.recipe_code))}</td>
            </tr>
            <tr>
              <td><b>Product Name:</b> ${esc(dash(ebookData.product_name))}</td>
              <td><b>Product Code:</b> ${esc(dash(ebookData.product_code))}</td>
              <td><b>Batch Size:</b> ${esc(dash(ebookData.batch_size))} ${esc(ebookData.batch_size_uom || '')}</td>
            </tr>
            <tr>
              <td><b>Status:</b> ${statusPill(ebookData.status)}</td>
              <td><b>Start:</b> ${esc(fmtDt(ebookData.start_time))}</td>
              <td><b>End:</b> ${esc(fmtDt(ebookData.end_time))}</td>
            </tr>
            <tr>
              <td><b>Created By:</b> ${esc(dash(ebookData.created_by))}</td>
              <td colspan="2"><b>Scheduled Date:</b> ${esc(fmtDate(ebookData.scheduled_date))}</td>
            </tr>
          </tbody>
        </table>

        ${approvalsHtml}
        ${processesHtml}
        ${auditHtml}

        <p style="margin-top:24px;font-size:10px;color:#777;text-align:right;">
          Generated on ${esc(formatDateTime(new Date().toISOString(), 'DD-MMM-YYYY HH:mm'))}
        </p>
      </div>
    </div>
  `;

  const container = document.createElement('div');
  container.innerHTML = html;
  const root = container.firstElementChild;
  if (!root) return false;

  // Inject shared A4 report CSS into the print window via printReport's REPORT_STYLE.
  // printReport already wraps with REPORT_STYLE; ensure styles exist in DOM for preview consistency.
  printReport(root, `Batch_LogBook_${ebookData.batch_number || 'report'}`);
  return true;
}

export { REPORT_STYLE };
